import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, Save, Loader2, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const CURRENT_POLICY_VERSION = '1.0';

const PrivacyConsentModal: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { emssUser, updateProfile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get current language to build the correct policy URL
  const langCode = i18n.language.split('-')[0] || 'pt';
  const policyUrl = `/privacy/policy-${langCode}.html`;

  useEffect(() => {
    // Show modal if user is logged in but hasn't accepted the current policy version
    if (emssUser && emssUser.privacy_policy_version !== CURRENT_POLICY_VERSION) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [emssUser]);

  const handleSave = async () => {
    if (!hasAccepted) {
      toast.error(t('privacy_modal.error_required'));
      return;
    }

    setIsSaving(true);
    try {
      await updateProfile({ 
        privacy_policy_version: CURRENT_POLICY_VERSION,
        privacy_policy_accepted_at: new Date().toISOString()
      });
      toast.success(t('privacy_modal.success'));
      setIsOpen(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('privacy_modal.error'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-hidden">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
        />
        
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative w-full max-w-lg bg-console-bg border border-white/10 rounded-[2.5rem] overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)]"
        >
          <div className="h-2 w-full bg-gradient-to-r from-game-primary via-white/50 to-game-primary animate-pulse" />
          
          <div className="p-8 md:p-10">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="relative">
                <div className="absolute inset-0 bg-game-primary/20 blur-2xl rounded-full" />
                <div className="relative w-20 h-20 bg-game-primary/10 rounded-3xl flex items-center justify-center border border-game-primary/20">
                  <ShieldCheck size={40} className="text-game-primary animate-pulse" />
                </div>
              </div>
              
              <div className="space-y-3">
                <h2 className="text-3xl font-condensed font-black italic uppercase tracking-tighter text-white">
                  {t('privacy_modal.title')}
                </h2>
                <p className="text-sm text-console-muted leading-relaxed">
                  {t('privacy_modal.description')}
                </p>
              </div>

              <div className="w-full space-y-6 pt-2">
                <a 
                  href={policyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-white text-sm font-medium group"
                >
                  {t('privacy_modal.read_button')}
                  <ExternalLink size={16} className="text-console-muted group-hover:text-white transition-colors" />
                </a>

                <label className="flex items-start gap-4 p-4 rounded-2xl border border-white/5 bg-console-surface cursor-pointer group hover:border-white/20 transition-all">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input 
                      type="checkbox" 
                      className="sr-only"
                      checked={hasAccepted}
                      onChange={(e) => setHasAccepted(e.target.checked)}
                    />
                    <div className={`w-6 h-6 rounded-lg border-2 transition-all flex items-center justify-center ${
                      hasAccepted ? 'bg-game-primary border-game-primary' : 'border-white/20 group-hover:border-white/40'
                    }`}>
                      {hasAccepted && <ShieldCheck size={14} className="text-black" />}
                    </div>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm text-white/90 font-medium">{t('privacy_modal.checkbox_label')}</p>
                    <p className="text-xs text-console-muted mt-1">
                      {t('privacy_modal.checkbox_hint')}
                    </p>
                  </div>
                </label>

                <button
                  onClick={handleSave}
                  disabled={!hasAccepted || isSaving}
                  className={`
                    w-full py-4 rounded-2xl font-label text-sm font-black uppercase tracking-[0.3em] flex items-center justify-center gap-3 transition-all duration-300
                    ${(!hasAccepted || isSaving)
                      ? 'bg-console-surface text-console-muted cursor-not-allowed opacity-50 border border-white/5' 
                      : 'bg-game-primary text-black hover:scale-[1.02] hover:brightness-110 active:scale-[0.98] shadow-[0_0_30px_rgba(var(--game-primary-rgb),0.4)] cursor-pointer'}
                  `}
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t('privacy_modal.saving')}
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {t('privacy_modal.accept_button')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
          
          <div className="bg-white/[0.02] py-4 text-center border-t border-white/[0.05]">
            <span className="text-[10px] font-label text-white/20 uppercase tracking-[0.5em]">
              {t('privacy_modal.footer_note', { version: CURRENT_POLICY_VERSION })}
            </span>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PrivacyConsentModal;
